// src/components/admin/AdminAnnulations.jsx
// Admin : qui annule, combien, pourquoi, et combien ça coûte à Thrundrz.

import React, { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { getSuiviAnnulations } from "../../services/AdminGestionService";

const euros = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`;

const LIBELLE_PAR = { acheteur: "Acheteur", organisateur: "Organisateur", admin: "Admin" };

const Carte = ({ label, valeur, rouge }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`text-2xl font-extrabold mt-1 ${rouge ? "text-red-600" : "text-gray-900"}`}>{valeur}</p>
    </div>
);

const AdminAnnulations = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erreur, setErreur] = useState(null);

    const charger = async () => {
        setLoading(true);
        try {
            setData(await getSuiviAnnulations());
            setErreur(null);
        } catch (e) {
            setErreur(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { charger(); }, []);

    if (loading) return <div className="p-8 text-gray-400">Chargement…</div>;
    if (erreur) return <div className="p-8 text-red-500">{erreur}</div>;

    const { totaux, organisateurs, dernieres } = data;

    return (
        <div className="w-full max-w-6xl mx-auto p-4 lg:p-8 font-Poppins bg-white">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-xl font-extrabold text-gray-900">Suivi des annulations</h1>
                    <p className="text-xs text-gray-500">Annulations par les organisateurs et coût pour Thrundrz</p>
                </div>
                <button onClick={charger} className="flex items-center gap-1.5 text-xs font-semibold text-violet-600">
                    <RefreshCw size={14} /> Actualiser
                </button>
            </div>

            {/* Totaux */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <Carte label="Réservations annulées par les organisateurs" valeur={totaux.reservations_annulees_orga} />
                <Carte label="Événements annulés" valeur={totaux.evenements_annules} />
                <Carte label="Remboursé aux acheteurs" valeur={euros(totaux.total_rembourse)} />
                <Carte label="Coût pour Thrundrz" valeur={euros(totaux.cout_thrundrz)} rouge />
            </div>

            {/* Par organisateur */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
                <h2 className="text-sm font-bold text-gray-900 mb-1">Par organisateur</h2>
                <p className="text-[11px] text-gray-500 mb-4">
                    Alerte si l'organisateur annule 10 % ou plus de ses billets, ou 10 réservations et plus.
                </p>
                {organisateurs.length === 0 ? (
                    <p className="text-sm text-gray-400 py-6 text-center">Aucune annulation par un organisateur.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                                    <th className="py-2 pr-3">Organisateur</th>
                                    <th className="py-2 pr-3">Réservations annulées</th>
                                    <th className="py-2 pr-3">Taux</th>
                                    <th className="py-2 pr-3">Événements annulés</th>
                                    <th className="py-2 pr-3">Remboursé</th>
                                    <th className="py-2 pr-3">Coût Thrundrz</th>
                                    <th className="py-2">Dernière</th>
                                </tr>
                            </thead>
                            <tbody>
                                {organisateurs.map((o) => (
                                    <tr key={o.user_id} className={`border-b border-gray-50 ${o.alerte ? "bg-red-50/60" : ""}`}>
                                        <td className="py-2.5 pr-3">
                                            <div className="flex items-center gap-1.5 font-semibold text-gray-900">
                                                {o.alerte && <AlertTriangle size={14} className="text-red-600" />}
                                                {o.nom}
                                            </div>
                                            <div className="text-[11px] text-gray-500">{o.email}</div>
                                        </td>
                                        <td className="py-2.5 pr-3">{o.nb_reservations_annulees}</td>
                                        <td className={`py-2.5 pr-3 font-semibold ${o.alerte ? "text-red-600" : ""}`}>{o.taux_annulation} %</td>
                                        <td className="py-2.5 pr-3">{o.nb_evenements_annules}</td>
                                        <td className="py-2.5 pr-3">{euros(o.total_rembourse)}</td>
                                        <td className="py-2.5 pr-3 text-red-600">{euros(o.cout_thrundrz)}</td>
                                        <td className="py-2.5 text-xs text-gray-500">{o.derniere_annulation}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Dernières annulations */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h2 className="text-sm font-bold text-gray-900 mb-4">50 dernières annulations</h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                                <th className="py-2 pr-3">Date</th>
                                <th className="py-2 pr-3">Billet</th>
                                <th className="py-2 pr-3">Événement</th>
                                <th className="py-2 pr-3">Par</th>
                                <th className="py-2 pr-3">Motif</th>
                                <th className="py-2 pr-3">Remboursé</th>
                                <th className="py-2">Coût</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dernieres.map((d) => (
                                <tr key={d.id} className="border-b border-gray-50 align-top">
                                    <td className="py-2.5 pr-3 text-xs text-gray-500 whitespace-nowrap">{d.date}</td>
                                    <td className="py-2.5 pr-3 font-mono text-xs">{d.reference}</td>
                                    <td className="py-2.5 pr-3">{d.evenement}</td>
                                    <td className="py-2.5 pr-3">{LIBELLE_PAR[d.annulee_par] || d.annulee_par}</td>
                                    <td className="py-2.5 pr-3">
                                        {d.motif || "—"}
                                        {d.commentaire && <div className="text-[11px] text-gray-500 italic">« {d.commentaire} »</div>}
                                    </td>
                                    <td className="py-2.5 pr-3">{euros(d.rembourse)}</td>
                                    <td className="py-2.5 text-red-600">{d.cout > 0 ? euros(d.cout) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default AdminAnnulations;